// ---------- Engine wire — generic_pricer ----------
// Mirrors immersive_gui/api/main.py: the same JSON-RPC method, instrument
// shapes, and response parser. The MVP routes through FastAPI; Diletta
// talks to the same Immersive WS directly. Architect rule E2 (native WS
// only) is preserved.
//
// Extracted verbatim from exotics-pricing-provider.tsx (IM-1594) so the
// Market Probabilities page and the Pricer page share one engine-wire surface. No
// behaviour change — the pricer re-exports these symbols.

import { Instrument } from '../../pages/exotics-pricer/contracts';

export interface SmilePoint {
  label: '10P' | '25P' | 'ATMF' | '25C' | '10C';
  vol: number;
}

export interface SabrParams {
  vol_0: number;
  xi: number;
  rho: number;
}

export interface LadderPayload {
  SHOCK: number[];
  SPOT: number[];
  PV: number[];
  DELTA: number[];
  GAMMA: number[];
  THETA: number[];
  VEGA: number[];
}

export interface PriceResult {
  pv: number;
  delta: number;
  gamma: number;
  theta: number;
  vega: number;
  spot: number;
  forward: number;
  smile: SmilePoint[];
  sabr: SabrParams;
  ladder?: LadderPayload;
}

export const SMILE_LABELS: SmilePoint['label'][] = ['10P', '25P', 'ATMF', '25C', '10C'];

// Splits a base_quote token string ('BTC', 'SOL_USDC', ...) into its base
// (underlying) and quote (accounting currency) parts. Unsplit identifiers
// fall back to USD per the comp-test convention
// (immersive_finance_environment_testing/tests/comprehensive/
//  test_all_endpoints.py:parse_token_pair).
export function splitBaseQuote(token: string): { base: string; quote: string } {
  return token.includes('_')
    ? { base: token.split('_', 2)[0], quote: token.split('_', 2)[1] }
    : { base: token, quote: 'USD' };
}

// Build the engine-side instrument JSON.
export function buildEngineInstrument(inst: Instrument): Record<string, unknown> {
  const { base, quote } = splitBaseQuote(inst.token);
  const acc = quote;
  // FastAPI pads bare YYYY-MM-DD with " 08:00:00".
  const exp = inst.expiry.length > 10 ? inst.expiry : `${inst.expiry} 08:00:00`;
  // Payout token follows notionalToken on contracts where it's choosable:
  // 'quote' -> acc currency, 'base' -> the underlying.
  const payoutToken = (i: Instrument): string =>
    'notionalToken' in i && i.notionalToken === 'base' ? base : acc;

  switch (inst.contract) {
    case 'EuropeanOption':
      return {
        type: 'european_option',
        und_token: base,
        acc_token: acc,
        expiry: exp,
        // Notional in underlying, same as Barrier — european_option's
        // payoff schema has no payout_token field (CONTRACT_SCHEMA forces
        // notionalChoosable: false), so it's omitted rather than sent.
        payoff: {
          strike: inst.strike,
          cp: inst.cp,
        },
      };
    case 'AmericanOption':
      return {
        type: 'american_option',
        und_token: base,
        acc_token: acc,
        expiry: exp,
        payoff: {
          strike: inst.strike,
          cp: inst.cp,
        },
      };
    case 'OneTouchUpper':
      return {
        type: 'one_touch',
        und_token: base,
        acc_token: acc,
        expiry: exp,
        payoff: {
          barrier: inst.upper,
          barrier_direction: 'upper',
          pay_type: inst.payType,
          payout: 1.0,
          payout_token: payoutToken(inst),
        },
      };
    case 'OneTouchLower':
      return {
        type: 'one_touch',
        und_token: base,
        acc_token: acc,
        expiry: exp,
        payoff: {
          barrier: inst.lower,
          barrier_direction: 'lower',
          pay_type: inst.payType,
          payout: 1.0,
          payout_token: payoutToken(inst),
        },
      };
    case 'SingleBarrierUpper':
      return {
        type: 'barrier_option',
        und_token: base,
        acc_token: acc,
        expiry: exp,
        payoff: {
          strike: inst.strike,
          cp: inst.cp,
          barrier: inst.upper,
          barrier_direction: 'upper',
        },
      };
    case 'SingleBarrierLower':
      return {
        type: 'barrier_option',
        und_token: base,
        acc_token: acc,
        expiry: exp,
        payoff: {
          strike: inst.strike,
          cp: inst.cp,
          barrier: inst.lower,
          barrier_direction: 'lower',
        },
      };
    case 'DoubleNoTouch':
      return {
        type: 'double_no_touch',
        und_token: base,
        acc_token: acc,
        expiry: exp,
        payoff: {
          upper_barrier: inst.upper,
          lower_barrier: inst.lower,
          payout: 1.0,
          payout_token: payoutToken(inst),
        },
      };
  }
}

// Coerce engine numeric outputs which may arrive as numbers, numeric
// strings, or percentage strings ("37.80%"). Percent strings are scaled
// back to decimal (so consumers using format="percent" — which expects a
// decimal — render correctly).
export function toNumber(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const trimmed = v.trim().replace(/,/g, '');
    if (!trimmed) return NaN;
    if (trimmed.endsWith('%')) {
      const n = Number(trimmed.slice(0, -1));
      return Number.isFinite(n) ? n / 100 : NaN;
    }
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : NaN;
  }
  return NaN;
}

// Parse the raw engine response into the typed PriceResult.
// Engine shape (from FastAPI main.py:272-298):
//   r.pv, r.risk.{delta,gamma,theta,vega,ladder?},
//   r.market.{spot, forward, display.<10P|25P|ATMF|25C|10C>},
//   r.calibration.display.{vol_0, xi, rho}
// Narrow an unknown wire value to an indexable record, or undefined. Keeps the
// parser free of `any` while still tolerating whatever the engine sends.
export function asRecord(v: unknown): Record<string, unknown> | undefined {
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : undefined;
}

export function parseEngineResponse(r: unknown): PriceResult | null {
  const obj = asRecord(r);
  if (!obj) return null;
  const mkt = asRecord(obj.market);
  const rsk = asRecord(obj.risk);
  if (!mkt || !rsk || obj.pv === undefined) return null;
  const cal = asRecord(asRecord(obj.calibration)?.display) ?? {};
  const display = asRecord(mkt.display) ?? {};

  const smile: SmilePoint[] = SMILE_LABELS.flatMap((label) => {
    const vol = toNumber(display[label]);
    return Number.isFinite(vol) ? [{ label, vol }] : [];
  });

  const result: PriceResult = {
    pv: toNumber(obj.pv),
    delta: toNumber(rsk.delta),
    gamma: toNumber(rsk.gamma),
    theta: toNumber(rsk.theta),
    vega: toNumber(rsk.vega),
    spot: toNumber(mkt.spot),
    forward: toNumber(mkt.forward),
    smile,
    sabr: {
      vol_0: toNumber(cal.vol_0),
      xi: toNumber(cal.xi),
      rho: toNumber(cal.rho),
    },
  };

  if (rsk.ladder) {
    result.ladder = rsk.ladder as LadderPayload;
  }
  return result;
}
